import {
  Car,
  CarManager,
  Cell,
  Facility,
  FacilityManager,
  House,
  HouseManager,
  PowerManager,
  SurfaceAnt,
  TerrainGrid,
  WaterManager,
} from '@antification/core';
import { antSpriteCache } from '../../formicarium/rendering/AntSpriteCache';
import { TerrainRenderer } from './TerrainRenderer';
import { CarRenderer } from './CarRenderer';
import { FacilityRenderer } from './FacilityRenderer';
import { HouseRenderer } from './HouseRenderer';
import { Camera, ScreenCoord } from './Camera';

/**
 * Context needed for entity rendering.
 */
export interface EntityRenderContext {
  camera: Camera;
  grid: TerrainGrid;
  cars: CarManager;
  houses: HouseManager;
  /** Which buildings the grid could not power at the last metered cycle. */
  power: PowerManager;
  /** Which buildings the mains could not water at the last metered cycle. */
  water: WaterManager;
  /**
   * What the polity has built here, and how much of its demand it got.
   *
   * A facility is drawn from this rather than from the grid's `SEALED` cells,
   * because the slab is the *ground* and the building on it is not: the terrain
   * cache is flat and blitted, and a block with a roof has to be depth-sorted
   * against the trees and traffic in front of it.
   */
  facilities: FacilityManager;
  surfaceAnts: () => Iterable<SurfaceAnt>;
  skipVegetation: boolean;
  skipHouses: boolean;
  skipCars: boolean;
  skipAnts: boolean;
}

/**
 * Type union for depth-sortable entities.
 */
type Renderable =
  | { type: 'vegetation'; entity: Cell; depth: number }
  | { type: 'house'; entity: House; depth: number }
  | { type: 'facility'; entity: Facility; depth: number }
  | { type: 'car'; entity: Car; depth: number }
  | { type: 'ant'; entity: SurfaceAnt; depth: number };

/**
 * Convert food hue (0-40) to RGB color values.
 * Hue 0 = ruby red, 20 = tangerine orange, 40 = golden yellow
 */
export function hueToFoodRGB(hue: number): { r: number; g: number; b: number } {
  if (hue < 15) {
    return { r: 220, g: 20 + hue * 3, b: 60 };
  } else if (hue < 30) {
    return { r: 255, g: 100 + (hue - 15) * 6, b: 30 };
  } else {
    return { r: 255, g: 200 + (hue - 30) * 2, b: 50 };
  }
}

/**
 * Renders depth-sorted entities (vegetation, houses, cars, ants).
 *
 * Owns nothing it draws. Trees, buildings and traffic each have a renderer of
 * their own; this class decides only what stands in front of what, which is a
 * question none of them can answer alone.
 */
export class EntityRenderer {
  constructor(
    private readonly terrain: TerrainRenderer,
    private readonly carRenderer: CarRenderer,
    private readonly houseRenderer: HouseRenderer,
    private readonly facilityRenderer: FacilityRenderer = new FacilityRenderer()
  ) {}

  /**
   * Render all entities in depth-sorted order.
   * Ensures proper occlusion in isometric view where entities at higher Y
   * (closer to viewer) should appear in front of entities at lower Y.
   */
  renderEntitiesDepthSorted(ctx: CanvasRenderingContext2D, rctx: EntityRenderContext): void {
    const renderables: Renderable[] = [];

    // Add vegetation (trees and bushes)
    if (!rctx.skipVegetation) {
      for (const { cell, depth } of rctx.grid.getVegetationWithDepth()) {
        renderables.push({ type: 'vegetation', entity: cell, depth });
      }
    }

    // Add houses
    if (!rctx.skipHouses) {
      for (const { house, depth } of rctx.houses.getHousesWithDepth()) {
        renderables.push({ type: 'house', entity: house, depth });
      }
    }

    // Add facilities. Not behind `skipHouses`: they are somebody else's
    // buildings, there are never more than a handful, and the performance flag
    // exists to measure the cost of the forty a district grows on its own.
    for (const facility of rctx.facilities.getFacilities()) {
      // Front edge of the footprint, as `getHousesWithDepth` does for a house.
      renderables.push({
        type: 'facility',
        entity: facility,
        depth: facility.y + facility.rows - 1,
      });
    }

    // Add cars
    if (!rctx.skipCars) {
      for (const { car, depth } of rctx.cars.getCarsWithDepth()) {
        renderables.push({ type: 'car', entity: car, depth });
      }
    }

    // Add surface ants
    if (!rctx.skipAnts) {
      for (const ant of rctx.surfaceAnts()) {
        const depth = ant.gridY + (ant.targetGridY - ant.gridY) * ant.progress;
        renderables.push({ type: 'ant', entity: ant, depth });
      }
    }

    // Sort by depth (lower Y = further back, rendered first)
    renderables.sort((a, b) => a.depth - b.depth);

    // Debug: expose entity counts globally for inspection
    (window as any).parkingtonRenderStats = {
      vegetation: renderables.filter(r => r.type === 'vegetation').length,
      houses: renderables.filter(r => r.type === 'house').length,
      facilities: renderables.filter(r => r.type === 'facility').length,
      cars: renderables.filter(r => r.type === 'car').length,
      ants: renderables.filter(r => r.type === 'ant').length,
      total: renderables.length,
    };

    // Render in sorted order
    for (const item of renderables) {
      switch (item.type) {
        case 'vegetation':
          this.terrain.renderVegetation(ctx, rctx.camera, rctx.grid, item.entity);
          break;
        case 'house':
          this.houseRenderer.render(
            ctx, rctx.camera, rctx.houses, item.entity,
            rctx.power.isShed(item.entity.id),
            rctx.water.isParched(item.entity.id)
          );
          break;
        case 'facility':
          this.facilityRenderer.render(
            ctx, rctx.camera, item.entity,
            rctx.facilities.getUptime().uptime,
            // What is stacked on a building site, and what a season of it takes.
            // Both are zero for a facility that has opened, and the renderer does
            // not look at them.
            rctx.facilities.getBuild(item.entity.id).hoard,
            rctx.facilities.goodsPerSeasonFor(item.entity.type)
          );
          break;
        case 'car':
          this.carRenderer.render(ctx, rctx.camera, rctx.grid, item.entity);
          break;
        case 'ant':
          this.renderSingleAnt(ctx, rctx.camera, item.entity);
          break;
      }
    }
  }

  /**
   * Render a single surface ant using pre-rendered sprite cache.
   *
   * The ant's position between two cells is interpolated here rather than read
   * off the ant. `ParkingtonSurfaceAnts` used to project both endpoints and
   * store the blend on every ant on every tick — the same arithmetic, done in
   * the simulation, for a camera the simulation should not have (ADR-009). The
   * cars have been drawn this way since ADR-008 moved `interpolateWithWrap`.
   */
  private renderSingleAnt(ctx: CanvasRenderingContext2D, camera: Camera, ant: SurfaceAnt): void {
    const scale = camera.getScale();

    ctx.save();

    // Very small size for surface ants (1 pixel base)
    const size = 1 * scale;

    // Calculate direction angle from SCREEN coordinates (not grid)
    const targetScreen = camera.gridToScreen(ant.targetGridX, ant.targetGridY);
    const currentScreen = camera.gridToScreen(ant.gridX, ant.gridY);
    const screenDirX = targetScreen.x - currentScreen.x;
    const screenDirY = targetScreen.y - currentScreen.y;
    const angle = Math.atan2(screenDirY, screenDirX);

    const pos: ScreenCoord = {
      x: currentScreen.x + screenDirX * ant.progress,
      y: currentScreen.y + screenDirY * ant.progress,
    };

    // Skip dead ants rendering (or show briefly)
    if (ant.state === 'dead') {
      ctx.globalAlpha = 0.3;
      ctx.fillStyle = '#2a1a10';
      ctx.beginPath();
      ctx.ellipse(pos.x, pos.y, size, size * 0.5, angle, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
      return;
    }

    // Fade in
    let alpha = 1;
    if (ant.age < 500) {
      alpha = ant.age / 500;
    }
    ctx.globalAlpha = alpha;

    // Try to use cached sprite
    const sprite = antSpriteCache.getSurfaceSprite(ant.colonyIndex, angle);

    if (sprite) {
      // Use cached sprite - single drawImage call instead of ~8 draw operations
      const refScale = 10;
      const drawScale = scale / refScale;

      const refSize = 1 * refScale;
      const padding = refSize * 2;
      const spriteWidth = refSize * 4 + padding * 2;
      const spriteHeight = refSize * 3 + padding * 2;

      const drawWidth = spriteWidth * drawScale;
      const drawHeight = spriteHeight * drawScale;

      ctx.drawImage(
        sprite.canvas,
        pos.x - drawWidth / 2,
        pos.y - drawHeight / 2,
        drawWidth,
        drawHeight
      );

      // Draw food indicator dynamically (color varies)
      if (ant.hasFood) {
        const { r, g, b } = hueToFoodRGB(ant.carriedFoodHue);
        ctx.fillStyle = `rgb(${r}, ${g}, ${b})`;
        ctx.beginPath();
        ctx.arc(pos.x, pos.y - size * 1.5, size * 0.4, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = 'rgba(255, 255, 200, 0.6)';
        ctx.beginPath();
        ctx.arc(pos.x - size * 0.1, pos.y - size * 1.6, size * 0.15, 0, Math.PI * 2);
        ctx.fill();
      }
    } else {
      // Fallback to direct rendering if cache not available
      this.renderSingleAntDirect(ctx, ant, pos, size, angle);
    }

    ctx.restore();
  }

  /**
   * Direct rendering fallback for surface ants.
   */
  private renderSingleAntDirect(
    ctx: CanvasRenderingContext2D,
    ant: SurfaceAnt,
    pos: ScreenCoord,
    size: number,
    angle: number
  ): void {
    const colonyColors = [
      { body: '#2a1810', outline: '#1a0d08' },
      { body: '#1a2020', outline: '#0a1010' },
      { body: '#3a1010', outline: '#200808' },
    ];
    const colors = colonyColors[ant.colonyIndex] || colonyColors[0];
    const bodyColor = colors.body;
    const outlineColor = colors.outline;

    ctx.fillStyle = bodyColor;
    ctx.strokeStyle = outlineColor;
    ctx.lineWidth = 0.5;

    const cos = Math.cos(angle);
    const sin = Math.sin(angle);

    // Abdomen (rear, largest)
    const abdomenX = pos.x - cos * size * 1.2;
    const abdomenY = pos.y - sin * size * 1.2;
    ctx.beginPath();
    ctx.ellipse(abdomenX, abdomenY, size * 1.1, size * 0.7, angle, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    // Thorax (middle)
    ctx.beginPath();
    ctx.ellipse(pos.x, pos.y, size * 0.7, size * 0.5, angle, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    // Head (front, smallest)
    const headX = pos.x + cos * size * 1.0;
    const headY = pos.y + sin * size * 1.0;
    ctx.beginPath();
    ctx.ellipse(headX, headY, size * 0.5, size * 0.4, angle, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    // Antennae
    ctx.strokeStyle = bodyColor;
    ctx.lineWidth = 0.5;
    const antennaLen = size * 0.8;
    ctx.beginPath();
    ctx.moveTo(headX, headY);
    ctx.lineTo(headX + Math.cos(angle - 0.5) * antennaLen, headY + Math.sin(angle - 0.5) * antennaLen);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(headX, headY);
    ctx.lineTo(headX + Math.cos(angle + 0.5) * antennaLen, headY + Math.sin(angle + 0.5) * antennaLen);
    ctx.stroke();

    // Food indicator
    if (ant.hasFood) {
      const { r, g, b } = hueToFoodRGB(ant.carriedFoodHue);
      ctx.fillStyle = `rgb(${r}, ${g}, ${b})`;
      ctx.beginPath();
      ctx.arc(pos.x, pos.y - size * 1.5, size * 0.4, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = 'rgba(255, 255, 200, 0.6)';
      ctx.beginPath();
      ctx.arc(pos.x - size * 0.1, pos.y - size * 1.6, size * 0.15, 0, Math.PI * 2);
      ctx.fill();
    }
  }
}
